/**
 * Iniciais para avatar sem foto: "Ana Souza" vira "AS".
 *
 * Extrai a primeira **letra** de cada palavra em vez de assumir que a palavra
 * começa com letra — um nome como "Collab (própria)" daria "C(" se pegasse o
 * primeiro caractere cru.
 *
 * Existe para que as telas não inventem cada uma a sua regra: já houve
 * `name.slice(0, 2)` (que fazia "new auth organization" virar "NE") e
 * `charAt(0)` (que rendia uma letra só), então o mesmo usuário aparecia grafado
 * de três jeitos em telas diferentes.
 *
 * ## O que o mobile perdeu ao adotar esta
 *
 * O filtro de creator do mobile usava `getInfluencerInitials`, que pega a
 * primeira e a **segunda** palavra com `charAt`. "Ana Maria Souza" saía "AM"
 * lá e "AS" aqui. Cheguei a injetar as duas por parâmetro para não escolher —
 * errado: o web é a fonte da verdade, então a regra dele é a certa e o mobile
 * se alinha.
 */
export function initialsOf(name: string): string {
  const letras = name
    .trim()
    .split(/\s+/)
    .map((palavra) => palavra.match(/\p{L}/u)?.[0])
    .filter((letra): letra is string => Boolean(letra));

  const primeira = letras[0];
  if (!primeira) return "?";

  const ultima = (letras.length > 1 ? letras[letras.length - 1] : "") ?? "";
  return (primeira + ultima).toUpperCase();
}

/*
 * As regras abaixo vieram de telas do web que tinham a sua própria, e
 * continuam dando exatamente as mesmas letras — trocar pelo `initialsOf` mudaria
 * letra na tela (decisão do dono de 2026-09-14: levar ao core sem mudar letra).
 * São quatro porque as quatro não dão o mesmo resultado para toda entrada.
 */

/**
 * Depoimentos da home (`testimonials/creator-initials.ts` do web): primeira
 * letra do primeiro e do último nome; nome único vira as duas primeiras letras;
 * vazio vira "?". `Array.from` para não partir caractere fora do BMP.
 */
export function testimonialInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return Array.from(parts[0] ?? "").slice(0, 2).join("").toUpperCase();
  const first = Array.from(parts[0] ?? "")[0] ?? "";
  const last = Array.from(parts[parts.length - 1] ?? "")[0] ?? "";
  return `${first}${last}`.toUpperCase();
}

/** Handle de rede social (modal de reordenar perfis): as duas primeiras letras depois do "@"; vazio vira "". */
export function socialHandleInitials(handle: string): string {
  return handle.replace(/^@/, "").slice(0, 2).toUpperCase();
}

/** PDF da calculadora: primeira letra das duas primeiras palavras separadas por espaço; sem nenhuma, "CR". */
export function pricingPdfInitials(name: string): string {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "CR";
}

/** PDF da proposta: primeira letra das duas primeiras palavras; sem nenhuma, "CR". */
export function proposalPdfInitials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "CR"
  );
}
